import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import NumPad from '../components/NumPad';

import { getBackendUrl } from '../../services/origin';
const API = `${getBackendUrl()}/api`;

export default function StaffLogin() {
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shaking, setShaking] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState(null);
  const { staffLogin } = useAuth();
  const { logo_url, primary_color, business_name, text_color, text_secondary_color, reloadBranding } = useBranding();
  const navigate = useNavigate();

  const isLocked = lockedUntil && new Date() < lockedUntil;

  useEffect(() => { reloadBranding(); }, []);

  const handleLogin = async () => {
    if (isLocked || loading || pin.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/staff/login`, { pin });
      staffLogin(res.data.token, res.data.staff_user);
      toast.success(`Benvenuto, ${res.data.staff_user.name}!`);
      navigate('/staff/home');
    } catch (err) {
      const newAtt = attempts + 1;
      setAttempts(newAtt);
      setPin('');
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
      if (newAtt >= 5) {
        const lockTime = new Date(Date.now() + 5 * 60 * 1000);
        setLockedUntil(lockTime);
        setError('Troppi tentativi errati. Attendi 5 minuti.');
      } else {
        setError(`PIN non corretto. Tentativi rimasti: ${5 - newAtt}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pin.length === 6 && !isLocked) handleLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  return (
    <div className="staff-app flex flex-col items-center justify-center min-h-dvh p-6">
      {/* Logo */}
      <div className="mb-10 text-center animate-fade-in-up">
        <div className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-4 overflow-hidden">
          <div
            className="w-full h-full rounded-3xl flex items-center justify-center shadow-lg bg-[#D44E20]"
            style={{ boxShadow: '0 10px 30px -5px rgba(212, 78, 32, 0.4)' }}
          >
            <span className="text-white font-black text-3xl tracking-tight">L</span>
          </div>
        </div>
        <h1 className="font-bold text-3xl tracking-tight" style={{ color: text_color || '#FFFFFF' }}>Lazo</h1>
        <p className="text-base mt-1" style={{ color: text_secondary_color || '#888888' }}>
          Accesso operatore
          {business_name && <span className="opacity-70"> · {business_name}</span>}
        </p>
      </div>

      {/* PIN dots */}
      <div className={`flex gap-4 mb-6 ${shaking ? 'animate-shake' : ''}`} data-testid="pin-display">
        {[0, 1, 2, 3, 4, 5].map(i => (
          <div
            key={i}
            className="w-4 h-4 rounded-full border-2 transition-all duration-200"
            style={pin.length > i ? {
              backgroundColor: primary_color,
              borderColor: primary_color,
              transform: 'scale(1.15)',
              boxShadow: `0 0 8px ${primary_color}40`,
            } : {
              borderColor: '#444',
            }}
          />
        ))}
      </div>

      {/* Error message */}
      {error && (
        <p className="text-[#FF5555] text-sm mb-4 text-center font-medium animate-fade-in" data-testid="login-error">
          {error}
        </p>
      )}
      {isLocked && !error && (
        <p className="text-[#FFAA00] text-sm mb-4 text-center font-medium">
          Account temporaneamente bloccato.
        </p>
      )}

      {/* NumPad */}
      <div className="w-full max-w-xs">
        <NumPad value={pin} onChange={setPin} maxLength={6} disabled={isLocked || loading} />
      </div>

      {/* Login button */}
      <button
        onClick={handleLogin}
        disabled={pin.length !== 6 || loading || isLocked}
        data-testid="staff-login-btn"
        className="mt-6 w-full max-w-xs h-14 text-white font-bold text-lg rounded-[14px] disabled:opacity-40 active:scale-95 transition-all"
        style={{ backgroundColor: primary_color, boxShadow: `0 8px 24px -4px ${primary_color}50` }}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Accesso in corso...
          </span>
        ) : 'Entra'}
      </button>

      <p className="text-[#555] text-xs mt-8 text-center">Solo per staff autorizzato</p>

      {/* Admin link */}
      <button
        onClick={() => navigate('/admin/login')}
        data-testid="go-to-admin-btn"
        className="mt-4 text-[#888] text-sm hover:text-white transition-colors underline underline-offset-2"
        style={{ '--hover-color': primary_color }}
      >
        Accedi come Admin
      </button>
    </div>
  );
}
